import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CheckCircle, XCircle, Clock, AlertTriangle, FileCheck,
  ThumbsUp, ThumbsDown, ExternalLink, Download,
  Loader2, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrency as fmtCur } from '@/lib/market';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ICON_MAP = { Clock, CheckCircle, XCircle, AlertTriangle };

const IC_STATUS_BADGE = {
  submitted:    'bg-[#ffd87a]/20 text-[#1a1a1a]',
  under_review: 'bg-[#ffd87a]/30 text-[#1a1a1a]',
  approved:     'bg-emerald-100 text-emerald-700',
};

const TABS = [
  { id: 'pending',  label: 'Pending' },
  { id: 'all',      label: 'All Requests' },
  { id: 'approved', label: 'Approved' },
];

export default function ApprovalsPage() {
  const { user } = useAuth();
  const { approvalStatusMap, approvalTypeMap } = useAppData();

  const [selectedTab, setSelectedTab] = useState('pending');

  // Generic approvals
  const [approvals, setApprovals]               = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showVoteDialog, setShowVoteDialog]     = useState(false);
  const [voteComment, setVoteComment]           = useState('');
  const [voting, setVoting]                     = useState(false);

  // IC Memos
  const [icMemos, setIcMemos]             = useState([]);
  const [approvedMemos, setApprovedMemos] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [allRes, allMemosRes, reviewerMemosRes] = await Promise.allSettled([
        axios.get(`${API}/approvals/requests`),
        axios.get(`${API}/deals/ic-memos`),
        user?.id ? axios.get(`${API}/deals/ic-memos?reviewer_id=${user.id}`) : Promise.resolve({ data: [] }),
      ]);

      if (allRes.status === 'fulfilled') setApprovals(allRes.value.data || []);

      const allMemos = allMemosRes.status === 'fulfilled' ? (allMemosRes.value.data || []) : [];
      // Pending = submitted or under_review (org-wide)
      setPendingApprovals(allMemos.filter(m => ['submitted', 'under_review'].includes(m.status)));
      // Approved IC memos
      setApprovedMemos(allMemos.filter(m => m.status === 'approved'));

      // IC memos assigned to current user as reviewer (for the IC Memos section in Pending tab)
      const reviewerMemos = reviewerMemosRes.status === 'fulfilled' ? (reviewerMemosRes.value.data || []) : [];
      setIcMemos(reviewerMemos.filter(m => ['submitted', 'under_review'].includes(m.status)));
    } catch {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (decision) => {
    if (!selectedApproval) return;
    setVoting(true);
    try {
      await axios.post(`${API}/approvals/requests/${selectedApproval.id}/vote`, { decision, comment: voteComment });
      toast.success(`Request ${decision === 'approve' ? 'approved' : 'rejected'}`);
      setShowVoteDialog(false);
      setSelectedApproval(null);
      setVoteComment('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Failed to submit vote');
    } finally {
      setVoting(false);
    }
  };

  const handleDownloadPDF = async (memo) => {
    setDownloadingId(memo.id);
    try {
      const res = await axios.get(`${API}/deals/ic-memos/${memo.id}/report`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IC_Memo_${(memo.deal_name || memo.id).replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatCurrency = (amount) => (!amount ? '-' : fmtCur(amount, user?.market));

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusInfo = (status) => {
    const row = approvalStatusMap[status] || approvalStatusMap['pending'] || {};
    return {
      label: row.label || status,
      color: row.data?.color || 'bg-[#ffd87a]',
      icon:  STATUS_ICON_MAP[row.data?.icon_name] || Clock,
    };
  };

  const getTypeInfo = (type) => {
    const row = approvalTypeMap[type] || approvalTypeMap['other'] || {};
    return { label: row.label || type, color: row.data?.color || 'text-[#4a4a4a]' };
  };

  const displayedApprovals = selectedTab === 'all' ? approvals : pendingApprovals;
  // All stats derived from IC memo data (org-scoped)
  const pendingCount   = pendingApprovals.length;                                         // submitted/under_review in org
  const approvedCount  = approvedMemos.length;                                            // approved in org
  const rejectedCount  = approvals.filter(a => a.status === 'rejected').length;           // generic rejections (kept for compat)
  const icPendingCount = icMemos.length;                                                  // pending for current user as reviewer
  const totalPending   = pendingCount;

  return (
    <div className="p-6" data-testid="approvals-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
          <CheckCircle className="w-5 h-5 text-[#1a1a1a]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Approvals</h1>
          <p className="text-[#4a4a4a] mt-1">Review and approve pending requests</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Pending Review',   value: totalPending,  color: 'text-[#ffd87a]', bg: 'bg-[#ffd87a]/10', Icon: Clock },
          { label: 'Approved',         value: approvedCount, color: 'text-emerald-600', bg: 'bg-emerald-50',  Icon: CheckCircle },
          { label: 'Rejected',         value: rejectedCount, color: 'text-red-400',     bg: 'bg-red-50',      Icon: XCircle },
          { label: 'IC Memos Pending', value: icPendingCount, color: 'text-[#ffd87a]', bg: 'bg-[#ffd87a]/10', Icon: FileText },
        ].map(({ label, value, color, bg, Icon }) => (
          <Card key={label} className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#4a4a4a] uppercase tracking-wide">{label}</p>
                  <p className="text-2xl font-bold text-[#1a1a1a] mt-1">{value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6">
        {TABS.map(tab => {
          const count = tab.id === 'pending' ? totalPending : tab.id === 'all' ? approvals.length : approvedCount;
          return (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedTab === tab.id
                  ? 'bg-[#ffd87a] text-[#1a1a1a] border border-[#ffd87a]'
                  : 'bg-[#f5f5f5] text-[#4a4a4a] hover:bg-[#e5e5e5] hover:text-[#1a1a1a]'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 text-xs ${selectedTab === tab.id ? 'text-[#1a1a1a]' : 'text-[#4a4a4a]'}`}>
                ({count})
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Pending tab — generic approvals + IC memos ─────────────────── */}
      {(selectedTab === 'pending' || selectedTab === 'all') && (
        <div className="space-y-4">
          {/* IC Memos section in Pending */}
          {selectedTab === 'pending' && icMemos.length > 0 && (
            <Card className="bg-white border-[#e5e5e5]">
              <CardHeader className="pb-3 border-b border-[#e5e5e5]">
                <CardTitle className="text-[#1a1a1a] text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#ffd87a]" />
                  IC Memos Awaiting Review
                  <span className="text-xs font-normal text-[#4a4a4a]">({icMemos.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <div className="space-y-2">
                  {icMemos.map(memo => (
                    <div key={memo.id} className="flex items-center gap-4 p-3 rounded-lg bg-white border border-[#e5e5e5] hover:shadow-sm transition-all">
                      <div className="w-9 h-9 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center shrink-0">
                        <FileText className="w-4 h-4 text-[#ffd87a]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#1a1a1a] truncate">{memo.title || 'IC Memo'}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${IC_STATUS_BADGE[memo.status] || 'bg-[#f5f5f5] text-[#4a4a4a]'}`}>
                            {memo.status?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-xs text-[#4a4a4a]">
                          {memo.company_name || memo.deal_name || ''}
                          {memo.submitted_at && ` · ${formatDate(memo.submitted_at)}`}
                        </p>
                      </div>
                      <Link to={`/deals/${memo.deal_id}`}>
                        <Button size="sm" className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] shrink-0 font-medium">
                          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Review
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* All pending IC memos (org-wide) — shown in Pending tab; generic requests in All tab */}
          <Card className="bg-white border-[#e5e5e5]">
            <CardHeader className="pb-3">
              <CardTitle className="text-[#1a1a1a] text-lg">
                {selectedTab === 'pending' ? 'Pending IC Memo Reviews' : 'All Approval Requests'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-16 bg-[#f9f9f9]/50 rounded-lg animate-pulse" />)}
                </div>
              ) : displayedApprovals.length === 0 ? (
                <div className="text-center py-10">
                  <FileCheck className="w-10 h-10 text-[#e5e5e5] mx-auto mb-3" />
                  <p className="text-[#4a4a4a] text-sm">
                    {selectedTab === 'pending' ? 'No pending IC memo reviews' : 'No approval requests'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {displayedApprovals.map((item) => {
                    const isMemo = item.deal_id !== undefined;
                    if (isMemo) {
                      return (
                        <div key={item.id} className="flex items-center gap-4 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:shadow-sm transition-all">
                          <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center shrink-0">
                            <FileText className="w-5 h-5 text-[#ffd87a]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-[#1a1a1a] font-medium truncate">{item.title || 'IC Memo'}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${IC_STATUS_BADGE[item.status] || 'bg-[#f5f5f5] text-[#4a4a4a]'}`}>
                                {item.status?.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-[#4a4a4a]">
                              {item.company_name || item.deal_name || ''}
                              {item.submitted_at && ` · ${formatDate(item.submitted_at)}`}
                            </p>
                          </div>
                          <Link to={`/deals/${item.deal_id}`}>
                            <Button size="sm" className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] font-medium shrink-0">
                              <ExternalLink className="w-4 h-4 mr-1" /> Open Deal
                            </Button>
                          </Link>
                        </div>
                      );
                    }
                    const statusInfo = getStatusInfo(item.status);
                    const typeInfo   = getTypeInfo(item.approval_type);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <div key={item.id}
                        className="flex items-center gap-4 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:shadow-sm transition-all"
                        data-testid={`approval-${item.id}`}>
                        <div className="w-10 h-10 rounded-lg bg-[#ffd87a]/10 flex items-center justify-center">
                          <StatusIcon className="w-5 h-5 text-[#1a1a1a]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-[#1a1a1a] font-medium truncate">{item.title || 'Approval Request'}</p>
                            <span className="px-2 py-0.5 rounded text-xs bg-[#f5f5f5] text-[#1a1a1a] border border-[#e5e5e5]">{statusInfo.label}</span>
                          </div>
                          <p className="text-sm text-[#4a4a4a] truncate">
                            <span className={typeInfo.color}>{typeInfo.label}</span>
                            {item.description && ` • ${item.description}`}
                          </p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-[#1a1a1a]">{formatDate(item.created_at)}</p>
                          <p className="text-xs text-[#4a4a4a]">Submitted</p>
                        </div>
                        {item.status === 'pending' && selectedTab === 'pending' && (
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] font-medium"
                              onClick={() => { setSelectedApproval(item); setShowVoteDialog(true); }}
                              data-testid={`approve-btn-${item.id}`}>
                              <ThumbsUp className="w-4 h-4 mr-1" /> Review
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Approved IC Memos tab ───────────────────────────────────────── */}
      {selectedTab === 'approved' && (
        <Card className="bg-white border-[#e5e5e5]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#1a1a1a] text-lg">Approved IC Memos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map(i => <div key={i} className="h-16 bg-[#f9f9f9]/50 rounded-lg animate-pulse" />)}
              </div>
            ) : approvedMemos.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-10 h-10 text-[#e5e5e5] mx-auto mb-3" />
                <p className="text-[#4a4a4a] text-sm">No approved IC Memos yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {approvedMemos.map(memo => (
                  <div key={memo.id} className="flex items-center gap-4 p-4 rounded-lg bg-white border border-[#e5e5e5] hover:shadow-sm transition-all">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#1a1a1a] font-medium truncate">{memo.title || 'IC Memo'}</p>
                      <p className="text-sm text-[#4a4a4a]">
                        {memo.company_name || memo.deal_name || ''}
                        {memo.approved_at && ` · Approved ${formatDate(memo.approved_at)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link to={`/deals/${memo.deal_id}`}>
                        <Button size="sm" variant="outline" className="border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5]">
                          <ExternalLink className="w-4 h-4 mr-1" /> View
                        </Button>
                      </Link>
                      <Button size="sm" className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] font-medium"
                        disabled={downloadingId === memo.id}
                        onClick={() => handleDownloadPDF(memo)}>
                        {downloadingId === memo.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <><Download className="w-4 h-4 mr-1" /> PDF</>}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vote Dialog */}
      <Dialog open={showVoteDialog} onOpenChange={setShowVoteDialog}>
        <DialogContent className="bg-white border-[#e5e5e5] text-[#1a1a1a]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">Review Approval Request</DialogTitle>
            <DialogDescription className="text-[#4a4a4a]">
              {selectedApproval?.title || 'Approval Request'}
            </DialogDescription>
          </DialogHeader>
          {selectedApproval && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-[#f9f9f9] border border-[#e5e5e5]">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#4a4a4a]">Type</p>
                    <p className="text-[#1a1a1a] font-medium">{getTypeInfo(selectedApproval.approval_type).label}</p>
                  </div>
                  {selectedApproval.amount && (
                    <div>
                      <p className="text-[#4a4a4a]">Amount</p>
                      <p className="text-[#1a1a1a] font-medium">{formatCurrency(selectedApproval.amount)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[#4a4a4a]">Submitted</p>
                    <p className="text-[#1a1a1a] font-medium">{formatDate(selectedApproval.created_at)}</p>
                  </div>
                </div>
                {selectedApproval.description && (
                  <div className="mt-4 pt-4 border-t border-[#e5e5e5]">
                    <p className="text-[#4a4a4a] text-sm">Description</p>
                    <p className="text-[#1a1a1a] text-sm mt-1">{selectedApproval.description}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm text-[#4a4a4a] mb-1 block">Comment (optional)</label>
                <Textarea
                  value={voteComment}
                  onChange={(e) => setVoteComment(e.target.value)}
                  placeholder="Add a comment for your decision..."
                  className="bg-[#f5f5f5] border-[#e5e5e5] text-[#1a1a1a] placeholder:text-[#4a4a4a]"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowVoteDialog(false)}
              className="border-[#e5e5e5] text-[#4a4a4a] hover:text-[#1a1a1a]">
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleVote('reject')} disabled={voting}
              className="border-[#e5e5e5] text-[#1a1a1a] hover:bg-[#f5f5f5]">
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
            <Button onClick={() => handleVote('approve')} disabled={voting}
              className="bg-[#ffd87a] hover:bg-[#ffd87a]/90 text-[#1a1a1a] font-medium"
              data-testid="confirm-approve-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              {voting ? 'Processing...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
